import { type ReactNode } from 'react'
import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest } from '../../../store/models'
import { baseQueryWithTokenRefresh } from '../../../common/baseQuery'
import { type Nullable } from '../../../common/Nullable'

import { parseJwt } from '../../../common/jwt'

const _BASE_URL = import.meta.env.REACT_APP_INSIGHTS_API_BASE_URL!

export type Dashboard = {
  id: string
  name: string
  domain: string
  token: string
  urlParams?: Record<string, unknown>
}

export type RawBiDashboard = {
  id: string
  name: string
  urlParams?: Record<string, unknown>
}

export type RawBiDashboardsResponse = {
  biToken: string
  dashboards: RawBiDashboard[]
}

export type DashboardFilter = {
  filterKey: string
  filterValue: string
  filterOperator: string
}

export interface DashboardRequest extends BaseRequest {
  filters?: Nullable<DashboardFilter[]>
  dashboardIds?: Nullable<string[]>
}

// ── Highlight types ──

export type TopicHighlight = {
  topic: string
  detail: ReactNode
}

export type MarketOutlookHighlight = {
  optimisticPercent: number
  neutralPercent: number
  concernedPercent: number
  summaryText: string
  strongestOpportunity: string
  topConcern: string
}

export type HeldAwayHighlight = {
  suggestedPercent: number
  agreedPercent: number
  summaryText: string
  leadMeetingType: string
  bestConvertMeetingType: string
}

export type ReferralHighlight = {
  commitRate: number
  summaryText: string
  leadAskMeetingType: string
  leadProvidedMeetingType: string
}

export type Highlights = {
  meetingCount: number
  mostDiscussed: TopicHighlight | null
  mostClientDriven: TopicHighlight | null
  mostTrending: TopicHighlight | null
  mostQuestionsRaised: TopicHighlight | null
  marketOutlook: MarketOutlookHighlight | null
  heldAwayAssets: HeldAwayHighlight | null
  referrals: ReferralHighlight | null
}

// ── Discussion Topics types ──

export type TrendDirection = 'up' | 'down' | 'neutral' | null

export type DiscussionTopicRow = {
  topic: string
  mentions: number
  clientDrivenMeetings: number
  clientDrivenPercent: number
  trendValue: number | null
  trendDirection: TrendDirection
  questionsRaised: number
}

export type InvestmentProductRow = {
  topic: string
  subTopic: string
  mentions: number
  clientDrivenMeetings: number
  clientDrivenPercent: number
  trendValue: number | null
  trendDirection: TrendDirection
  questionsRaised: number
}

// ── Client Outlook types ──

export type AssetClassOutlookRow = {
  assetClass: string
  optimisticPercent: number
  optimisticBarWidth: number
  concernedPercent: number
  concernedBarWidth: number
}

export type ClientOutlook = {
  optimisticRows: AssetClassOutlookRow[]
  concernedRows: AssetClassOutlookRow[]
  totalAssetClasses: number
}

// ── Asset Opportunities types ──

export type AssetOpportunityMetrics = {
  assetMentions: number
  assetMentionsTrend: number
  assetMentionsLeadMeetingType: string
  suggestionsMade: number
  suggestionsMadeTrend: number
  suggestionsLeadMeetingType: string
  clientAgreed: number
  clientAgreedTrend: number
  clientAgreedLeadMeetingType: string
}

export type AssetOpportunityRow = {
  meetingType: string
  assetMentions: number
  assetMentionsTrend: number
  suggestionMade: number
  suggestionMadePct: number
  suggestionMadeTrend: number
  clientAgreed: number
  clientAgreedPct: number
  clientAgreedTrend: number
  effectiveSuggestedForRate: number
  effectiveAgreedForRate: number
}

export type AssetOpportunities = {
  metrics: AssetOpportunityMetrics
  rows: AssetOpportunityRow[]
}

// ── Referrals types ──

export type ReferralMetrics = {
  referralsAsked: number
  referralsAskedTrend: number
  referralsAskedLeadMeetingType: string
  referralsProvided: number
  referralsProvidedTrend: number
  referralsProvidedLeadMeetingType: string
}

export type ReferralRow = {
  meetingType: string
  meetings: number
  referralsAsked: number
  referralsAskedTrend: number
  referralsProvided: number
  referralsProvidedTrend: number
  referralsAskedForRate: number
  referralsProvidedForRate: number
}

export type Referrals = {
  metrics: ReferralMetrics
  rows: ReferralRow[]
  isPartialData: boolean
}

// ── Dashboard data ──

export type InsightsDashboardData = {
  timeRange: string
  highlights: Highlights
  discussionTopics: DiscussionTopicRow[]
  investmentProducts: InvestmentProductRow[]
  clientOutlook: ClientOutlook | null
  assetOpportunities: AssetOpportunities | null
  referrals: Referrals | null
}

// ── API types ──

export type TopicStatRow = {
  topic: string
  total_meetings_current: number
  total_meetings_previous: number
  meetings_with_topic_current: number
  meetings_with_topic_previous: number
  total_mentions_current: number
  total_mentions_previous: number
  questions_current: number
  questions_previous: number
  client_driven_pct_current: number
  client_driven_meetings_current: number
  client_driven_meetings_previous: number
  client_driven_pct_point_change: number
}

export type InvestmentProductStatRow = {
  sub_topic: string | null
  total_meetings_current: number
  total_meetings_previous: number
  meetings_with_subtopic_current: number
  meetings_with_subtopic_previous: number
  total_mentions_current: number
  total_mentions_previous: number
  questions_current: number
  questions_previous: number
  client_driven_pct_current: number
  client_driven_meetings_current: number
  client_driven_meetings_previous: number
  client_driven_pct_point_change: number
}

export const DISCUSSION_TAB_CHARTS = ['topic_stats', 'investment_product_stats', 'held_away_assets', 'referrals', 'client_outlook']

export type UserScorecardHistoryPoint = {
  date: string
  score: number
  min_score: number
  max_score: number
  meeting_count: number
}

export type UserScoreRow = {
  user_id: string
  user_name: string
  user_email: string
  meetings_current: number
  meetings_previous: number
  meetings_trend: number
  avg_score_current: number | null
  avg_score_previous: number | null
  avg_score_trend: number | null
  min_score_current: number | null
  max_score_current: number | null
  score_history: UserScorecardHistoryPoint[]
}

// A flagged meeting behind a scorecard criterion's improvement count, for drill-down links
export type ScoreResultMeetingRef = {
  room_id: string
  session_id: string
  score: number
  title: string
  date: string
}

// One row per user for the queried date range, with a flagged-as-needing-improvement count + the flagged meetings for every scorecard criterion
export type UserScoreResultRow = {
  user_id: string
  user_name: string
  user_email: string

  meetings: number

  wf_nextStepsImprovementCount: number
  wf_nextStepsPreviousImprovementCount: number
  wf_nextStepsImprovementSessions: ScoreResultMeetingRef[]
  wf_lifeEventsImprovementCount: number
  wf_lifeEventsPreviousImprovementCount: number
  wf_lifeEventsImprovementSessions: ScoreResultMeetingRef[]
  wf_followUpTasksImprovementCount: number
  wf_followUpTasksPreviousImprovementCount: number
  wf_followUpTasksImprovementSessions: ScoreResultMeetingRef[]
  wf_meetingAgendaImprovementCount: number
  wf_meetingAgendaPreviousImprovementCount: number
  wf_meetingAgendaImprovementSessions: ScoreResultMeetingRef[]
  wf_coreMotivatorsImprovementCount: number
  wf_coreMotivatorsPreviousImprovementCount: number
  wf_coreMotivatorsImprovementSessions: ScoreResultMeetingRef[]
  wf_decisionsAgreedImprovementCount: number
  wf_decisionsAgreedPreviousImprovementCount: number
  wf_decisionsAgreedImprovementSessions: ScoreResultMeetingRef[]
  wf_decisionsNotAgreedImprovementCount: number
  wf_decisionsNotAgreedPreviousImprovementCount: number
  wf_decisionsNotAgreedImprovementSessions: ScoreResultMeetingRef[]

  eng_sentimentImprovementCount: number
  eng_sentimentPreviousImprovementCount: number
  eng_sentimentImprovementSessions: ScoreResultMeetingRef[]
  eng_avgTalkTimeImprovementCount: number
  eng_avgTalkTimePreviousImprovementCount: number
  eng_avgTalkTimeImprovementSessions: ScoreResultMeetingRef[]
  eng_questionRateImprovementCount: number
  eng_questionRatePreviousImprovementCount: number
  eng_questionRateImprovementSessions: ScoreResultMeetingRef[]
  eng_clientQuestionsImprovementCount: number
  eng_clientQuestionsPreviousImprovementCount: number
  eng_clientQuestionsImprovementSessions: ScoreResultMeetingRef[]
  eng_clientConnectionImprovementCount: number
  eng_clientConnectionPreviousImprovementCount: number
  eng_clientConnectionImprovementSessions: ScoreResultMeetingRef[]
  eng_personalEngagementImprovementCount: number
  eng_personalEngagementPreviousImprovementCount: number
  eng_personalEngagementImprovementSessions: ScoreResultMeetingRef[]
  eng_avoidedLongMonologueImprovementCount: number
  eng_avoidedLongMonologuePreviousImprovementCount: number
  eng_avoidedLongMonologueImprovementSessions: ScoreResultMeetingRef[]
  eng_interactiveConversationImprovementCount: number
  eng_interactiveConversationPreviousImprovementCount: number
  eng_interactiveConversationImprovementSessions: ScoreResultMeetingRef[]

  sch_calendarEventImprovementCount: number
  sch_calendarEventPreviousImprovementCount: number
  sch_calendarEventImprovementSessions: ScoreResultMeetingRef[]
  sch_overUnderTimeImprovementCount: number
  sch_overUnderTimePreviousImprovementCount: number
  sch_overUnderTimeImprovementSessions: ScoreResultMeetingRef[]
  sch_futureMeetingsImprovementCount: number
  sch_futureMeetingsPreviousImprovementCount: number
  sch_futureMeetingsImprovementSessions: ScoreResultMeetingRef[]

  top_meetings: ScoreResultMeetingRef[]
}

export type InsightsDataCharts = {
  client_outlook?: Array<{
    asset_class: string
    optimistic: number
    neutral: number
    concerned: number
    total_count: number
    bullish_count: number
    neutral_count: number
    bearish_count: number
  }>
  referrals?: Array<{
    meeting_type: string
    meetings_current: number
    referrals_asked_current: number
    referrals_asked_previous: number
    referrals_asked_trend: number
    referrals_provided_current: number
    referrals_provided_previous: number
    referrals_provided_trend: number
    client_referral_asked_count_current: number
    client_referral_asked_count_previous: number
    referee_count_current: number
    referee_count_previous: number
  }>
  held_away_assets?: Array<{
    template_name: string
    asset_mentioned_current: number
    asset_mentioned_previous: number
    asset_mentioned_trend: number
    suggestion_made_current: number
    suggestion_made_previous: number
    suggestion_made_trend: number
    client_agreed_current: number
    client_agreed_previous: number
    client_agreed_trend: number
  }>
  topic_stats?: TopicStatRow[]
  investment_product_stats?: InvestmentProductStatRow[]
  user_score_list?: UserScoreRow[]
  user_score_result_list?: UserScoreResultRow[]
}

export type InsightsDataApiResponse = {
  from?: string
  to?: string
  charts?: InsightsDataCharts
}

export const insightsApi = createApi({
  reducerPath: 'insightsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Insights'],
  endpoints: (builder) => ({
    getBiDashboards: builder.query<Dashboard[], DashboardRequest>({
      query: ({ accountId, filters, dashboardIds }) => ({
        url: `/v1/accounts/${accountId}/bi-dashboards`,
        method: 'POST',
        body: {
          filters: filters,
          internalIds: dashboardIds,
        },
      }),
      transformResponse: (response: RawBiDashboardsResponse) => {
        const { dashboards, biToken } = response
        const token = parseJwt(biToken)
        return dashboards.map((d) => ({
          id: d.id,
          name: d.name,
          domain: token.aud,
          token: biToken,
          urlParams: d.urlParams,
        }))
      },
    }),
  }),
})

export const { useGetBiDashboardsQuery } = insightsApi
